import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { X, Loader, CheckSquare, Square, CheckCircle, XCircle } from 'lucide-react';
import { OCUPANTES_ACTIVOS_PARA_CUENTAS, GENERAR_CUENTAS_COBRO } from '../../graphql/cuentasCobro';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function formatMoneda(v) {
  if (v == null) return '$0';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
}

function ModalGenerarCuentas({ periodoMes, periodoAnio, onClose, onSuccess }) {
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [todosSeleccionados, setTodosSeleccionados] = useState(true);
  const [mensaje, setMensaje] = useState(null); // { tipo: 'exito'|'error', texto }

  const { data, loading } = useQuery(OCUPANTES_ACTIVOS_PARA_CUENTAS, {
    variables: { periodo_mes: periodoMes, periodo_anio: periodoAnio },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      // Pre-seleccionar todos los que NO tienen cuenta aún
      const disponibles = (data?.ocupantesActivosParaCuentas || [])
        .filter(o => !o.ya_tiene_cuenta)
        .map(o => o.id);
      setSeleccionados(new Set(disponibles));
    }
  });

  const [generarCuentas, { loading: generando }] = useMutation(GENERAR_CUENTAS_COBRO);

  const ocupantes = data?.ocupantesActivosParaCuentas || [];
  const disponibles = ocupantes.filter(o => !o.ya_tiene_cuenta);
  const yaTienen = ocupantes.filter(o => o.ya_tiene_cuenta);

  const dias = new Date(periodoAnio, periodoMes, 0).getDate();

  const toggleSeleccion = (id) => {
    const nuevo = new Set(seleccionados);
    if (nuevo.has(id)) {
      nuevo.delete(id);
    } else {
      nuevo.add(id);
    }
    setSeleccionados(nuevo);
    setTodosSeleccionados(nuevo.size === disponibles.length);
  };

  const toggleTodos = () => {
    if (todosSeleccionados) {
      setSeleccionados(new Set());
      setTodosSeleccionados(false);
    } else {
      setSeleccionados(new Set(disponibles.map(o => o.id)));
      setTodosSeleccionados(true);
    }
  };

  const handleGenerar = async () => {
    try {
      const input = {
        periodo_mes: periodoMes,
        periodo_anio: periodoAnio,
      };

      // Si no están todos seleccionados, enviar IDs específicos
      if (seleccionados.size < disponibles.length) {
        input.ocupante_ids = Array.from(seleccionados);
      }

      const { data: result, errors } = await generarCuentas({ variables: { input } });
      if (errors && errors.length > 0) {
        setMensaje({ tipo: 'error', texto: errors[0].message });
        return;
      }
      const n = result?.generarCuentasCobro?.cuentas_generadas || 0;
      setMensaje({ tipo: 'exito', texto: `Se generaron ${n} cuentas de cobro para ${MESES[periodoMes - 1]} ${periodoAnio}` });
      setTimeout(() => onSuccess(), 1500);
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.message });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-cc modal-cc--lg" onClick={e => e.stopPropagation()}>
        <div className="modal-cc__header">
          <h2>Generar Cuentas de Cobro - {MESES[periodoMes - 1]} {periodoAnio}</h2>
          <button className="btn-cerrar" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-cc__body">
          {loading ? (
            <div className="loading-overlay">
              <Loader size={32} className="spinner" />
              <p>Cargando soldados activos...</p>
            </div>
          ) : (
            <>
              <div className="generar-info">
                <p><strong>Periodo:</strong> {MESES[periodoMes - 1]} {periodoAnio} ({dias} días)</p>
                <p><strong>Soldados activos:</strong> {ocupantes.length} | <strong>Disponibles:</strong> {disponibles.length} | <strong>Ya generadas:</strong> {yaTienen.length}</p>
              </div>

              {disponibles.length === 0 ? (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  <p className="empty-state__title">Todos los soldados ya tienen cuenta para este periodo</p>
                </div>
              ) : (
                <>
                  <div className="generar-toolbar">
                    <button className="btn-seleccionar-todos" onClick={toggleTodos}>
                      {todosSeleccionados ? <CheckSquare size={16} /> : <Square size={16} />}
                      {todosSeleccionados ? 'Deseleccionar todos' : 'Seleccionar todos'}
                    </button>
                    <span className="seleccionados-count">{seleccionados.size} de {disponibles.length} seleccionados</span>
                  </div>

                  <div className="lista-ocupantes">
                    {disponibles.map(oc => (
                      <div
                        key={oc.id}
                        className={`ocupante-item ${seleccionados.has(oc.id) ? 'ocupante-item--seleccionado' : ''}`}
                        onClick={() => toggleSeleccion(oc.id)}
                      >
                        <div className="ocupante-check">
                          {seleccionados.has(oc.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                        </div>
                        <div className="ocupante-datos">
                          <strong>{oc.nombre}</strong>
                          <small>{oc.grado_nombre || 'Sin grado'} - Doc: {oc.numero_documento || 'N/A'}</small>
                        </div>
                        <div className="ocupante-ubicacion">
                          Hab {oc.habitacion_numero} / Cama {oc.cama_numero || '-'}
                        </div>
                        <div className="ocupante-monto">
                          <span>{formatMoneda(oc.precio_noche)}/noche</span>
                          <strong>{formatMoneda(oc.precio_noche * dias)}</strong>
                        </div>
                        {oc.monto_anticipo > 0 && (
                          <div className="ocupante-anticipo">
                            Anticipo: {formatMoneda(oc.monto_anticipo)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {yaTienen.length > 0 && (
                <div className="ya-generadas">
                  <p className="ya-generadas__titulo">Ya tienen cuenta generada ({yaTienen.length}):</p>
                  <div className="ya-generadas__lista">
                    {yaTienen.map(oc => (
                      <span key={oc.id} className="ya-generada-tag">{oc.nombre}</span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {mensaje && (
          <div className={`generar-mensaje generar-mensaje--${mensaje.tipo}`}>
            {mensaje.tipo === 'exito' ? <CheckCircle size={18} /> : <XCircle size={18} />}
            <span>{mensaje.texto}</span>
          </div>
        )}

        <div className="modal-cc__footer">
          <button className="btn-modal" onClick={onClose}>Cancelar</button>
          <button
            className="btn-modal btn-modal--primary"
            onClick={handleGenerar}
            disabled={generando || seleccionados.size === 0}
          >
            {generando ? (
              <><Loader size={16} className="spinner" /> Generando...</>
            ) : (
              `Generar ${seleccionados.size} Cuentas`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Estilos inline del modal generar */
const style = document.createElement('style');
style.textContent = `
.generar-info { margin-bottom: 1rem; }
.generar-info p { margin: 0.25rem 0; font-size: 0.9rem; color: var(--hotel-text); }
.generar-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; padding: 0.5rem 0; border-bottom: 1px solid var(--hotel-border); }
.btn-seleccionar-todos { display: flex; align-items: center; gap: 0.4rem; background: none; border: none; color: var(--hotel-primary); cursor: pointer; font-weight: 600; font-size: 0.85rem; padding: 0; }
.seleccionados-count { font-size: 0.8rem; color: var(--hotel-text-secondary); }
.lista-ocupantes { max-height: 400px; overflow-y: auto; display: flex; flex-direction: column; gap: 0.5rem; }
.ocupante-item { display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem; border: 1px solid var(--hotel-border); border-radius: 8px; cursor: pointer; transition: all 0.15s; }
.ocupante-item:hover { background: var(--hotel-background); }
.ocupante-item--seleccionado { border-color: var(--hotel-primary); background: rgba(59, 130, 246, 0.05); }
.ocupante-check { color: var(--hotel-text-secondary); flex-shrink: 0; }
.ocupante-item--seleccionado .ocupante-check { color: var(--hotel-primary); }
.ocupante-datos { flex: 1; display: flex; flex-direction: column; }
.ocupante-datos strong { font-size: 0.875rem; }
.ocupante-datos small { font-size: 0.75rem; color: var(--hotel-text-secondary); }
.ocupante-ubicacion { font-size: 0.8rem; color: var(--hotel-text-secondary); white-space: nowrap; }
.ocupante-monto { text-align: right; display: flex; flex-direction: column; }
.ocupante-monto span { font-size: 0.75rem; color: var(--hotel-text-secondary); }
.ocupante-monto strong { font-size: 0.9rem; color: var(--hotel-text); }
.ocupante-anticipo { font-size: 0.75rem; color: #22c55e; font-weight: 600; white-space: nowrap; }
.ya-generadas { margin-top: 1rem; padding-top: 1rem; border-top: 1px solid var(--hotel-border); }
.ya-generadas__titulo { font-size: 0.8rem; color: var(--hotel-text-secondary); font-weight: 600; margin-bottom: 0.5rem; }
.ya-generadas__lista { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.ya-generada-tag { display: inline-block; padding: 0.2rem 0.5rem; background: var(--hotel-background); border-radius: 4px; font-size: 0.75rem; color: var(--hotel-text-secondary); }
.generar-mensaje { display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1rem; margin: 0 1.25rem; border-radius: 8px; font-size: 0.875rem; font-weight: 500; }
.generar-mensaje--exito { background: rgba(34, 197, 94, 0.1); color: #16a34a; }
.generar-mensaje--error { background: rgba(239, 68, 68, 0.1); color: #dc2626; }
`;
if (!document.getElementById('modal-generar-styles')) {
  style.id = 'modal-generar-styles';
  document.head.appendChild(style);
}

export default ModalGenerarCuentas;
